/**
 * Research Service
 * Handles AI-powered research functionality for clocks
 * VERSION: 2025-11-07 - Added URL fragment sharing (#research) for shareable links
 */
console.log('🚀 deep-research-service.js LOADED');
console.log('🔍 Script loading timestamp:', new Date().toISOString());

class DeepResearchService {
    constructor() {
        console.log('🔧 DeepResearchService constructor called');
        this.modal = null;
        this.container = null;
        this.closeBtn = null;
        this.isProcessing = false;
        this.currentClockId = null;
        this.currentResearchId = null;
        this.pollingInterval = null;
        
        this.init();
    }

    /**
     * Initialize the research service
     */
    init() {
        console.log('🔧 DeepResearchService.init() called');
        console.log('🔍 Document ready state:', document.readyState);

        this.modal = document.getElementById('deepResearchModal');
        this.container = document.getElementById('deepResearchContainer');
        this.closeBtn = document.getElementById('closeDeepResearch');

        console.log('🔍 Modal element found:', !!this.modal);
        console.log('🔍 Container element found:', !!this.container);
        console.log('🔍 Close button found:', !!this.closeBtn);

        if (!this.modal || !this.container || !this.closeBtn) {
            console.error('❌ Research modal elements not found');
            return;
        }

        this.setupEventListeners();

        // Check URL hash on page load - auto-open modal if #research is present
        this.checkUrlHashOnInit();

        console.log('✅ Research service initialized successfully');
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        console.log('🔧 Setting up research event listeners');

        // Close button
        this.closeBtn.addEventListener('click', () => {
            console.log('🔧 Close button clicked');
            this.closeModal();
        });

        // Close on backdrop click
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                console.log('🔧 Backdrop clicked');
                this.closeModal();
            }
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.classList.contains('active')) {
                console.log('🔧 Escape key pressed');
                this.closeModal();
            }
        });

        // Listen for browser back/forward button
        window.addEventListener('popstate', (e) => {
            console.log('🔧 Popstate event - hash:', window.location.hash);
            this.handleHashChange();
        });

        // Setup edit event listeners (delegated to handle dynamically added content)
        this.setupEditEventListeners();
    }

    /**
     * Show the research modal
     * @param {string} clockId - The clock ID to research
     */
    async showModal(clockId, forceNew = false) {
        console.log('🔧 DeepResearchService.showModal() called with clockId:', clockId, 'forceNew:', forceNew);

        if (this.isProcessing && !forceNew) {
            console.log('⚠️ Research already in progress');
            return;
        }

        // If forcing new research, reset processing state
        if (forceNew) {
            this.isProcessing = false;
            this.currentResearchId = null;
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        }

        this.currentClockId = clockId;
        this.modal.classList.add('active');

        // Update URL hash for shareable link
        this.updateUrlHash();

        // Show loading state
        this.showLoadingState();

        try {
            // Start the research
            await this.startResearch(clockId, forceNew);
        } catch (error) {
            console.error('❌ Failed to start research:', error);
            this.showErrorState(error.message);
        }
    }

    /**
     * Show existing research data
     * @param {string} clockId - The clock ID to show research for
     */
    async showExistingResearch(clockId) {
        console.log('🔧 DeepResearchService.showExistingResearch() called with clockId:', clockId);

        this.currentClockId = clockId;
        this.modal.classList.add('active');

        // Update URL hash for shareable link
        this.updateUrlHash();

        // Show loading state
        this.showLoadingState();

        try {
            const DEEP_RESEARCH_API_URL = `${window.CONFIG.aws.deepResearch.baseUrl}${window.CONFIG.aws.deepResearch.endpoints.status}`;
            const response = await fetch(`${DEEP_RESEARCH_API_URL}?clockId=${clockId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('📥 Existing research response:', result);

            if (result.success && result.status === 'completed' && result.data) {
                console.log('✅ Showing existing research data');
                this.showExistingResearchResults(result.data);
            } else if (['in_progress', 'started', 'processing', 'pending'].includes(result.status)) {
                // A run is in flight (e.g. the #research deep-link was opened mid-research). Show progress
                // and join the poll instead of erroring — the result lands when the backend finishes.
                console.log('⏳ Research in progress — showing progress + polling');
                this.isProcessing = true;
                this.showProcessingState();
                this.startPolling(clockId);
            } else if (result.status === 'failed') {
                this.showErrorState(result.error || 'The last research run failed. Try again.');
            } else {
                // No research has been run for this clock yet — neutral state, not a scary error.
                this.showNoResearchState();
            }

        } catch (error) {
            console.error('❌ Failed to load existing research:', error);
            this.showErrorState(error.message);
        }
    }

    /**
     * Close the modal
     */
    closeModal() {
        console.log('🔧 DeepResearchService.closeModal() called');

        this.modal.classList.remove('active');
        this.isProcessing = false;
        this.currentClockId = null;
        this.currentResearchId = null;

        // Clear any polling
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }

        // Clear URL hash if it's currently #research
        this.clearUrlHash();

        // Clear container
        this.container.innerHTML = '';
    }

    /**
     * Update URL hash to make the modal shareable
     */
    updateUrlHash() {
        if (window.location.hash !== '#research') {
            console.log('🔗 Setting URL hash to #research');
            history.pushState(null, '', window.location.pathname + window.location.search + '#research');
        }
    }

    /**
     * Clear URL hash when closing modal
     */
    clearUrlHash() {
        if (window.location.hash === '#research') {
            console.log('🔗 Clearing URL hash');
            history.pushState(null, '', window.location.pathname + window.location.search);
        }
    }

    /**
     * Check URL hash on page load and auto-open modal if #research is present
     */
    checkUrlHashOnInit() {
        if (window.location.hash === '#research') {
            console.log('🔗 Found #research hash on page load, auto-opening modal');

            // Wait for clock data to be loaded, then open modal
            this.waitForClockDataAndOpen();
        }
    }

    /**
     * Wait for clock data to be available, then open research modal
     */
    waitForClockDataAndOpen() {
        const maxAttempts = 50; // 5 seconds max (50 * 100ms)
        let attempts = 0;

        const checkClockData = () => {
            attempts++;

            // Check if view-clock page has loaded clock data
            if (window.clockViewPage && window.clockViewPage.clock && window.clockViewPage.clock.id) {
                const clockId = window.clockViewPage.clock.id;
                console.log('✅ Clock data loaded, opening research modal for:', clockId);
                this.showExistingResearch(clockId);
                return;
            }

            // If max attempts reached, give up
            if (attempts >= maxAttempts) {
                console.warn('⚠️ Timeout waiting for clock data to load');
                return;
            }

            // Try again in 100ms
            setTimeout(checkClockData, 100);
        };

        // Start checking
        checkClockData();
    }

    /**
     * Handle hash change from browser navigation (back/forward buttons)
     */
    handleHashChange() {
        if (window.location.hash === '#research') {
            // Hash changed to #research - open modal if not already open
            if (!this.modal.classList.contains('active')) {
                // Try to get clock ID from query params (legacy) or from page context (clean URLs)
                const urlParams = new URLSearchParams(window.location.search);
                const clockId = urlParams.get('id') || window.clockViewPage?.clock?.id;

                if (clockId) {
                    this.showExistingResearch(clockId);
                }
            }
        } else {
            // Hash changed away from #research - close modal if open
            if (this.modal.classList.contains('active')) {
                // Don't call this.closeModal() as it would update hash again
                // Just close the modal directly
                this.modal.classList.remove('active');
                this.isProcessing = false;
                this.currentClockId = null;
                this.currentResearchId = null;

                if (this.pollingInterval) {
                    clearInterval(this.pollingInterval);
                    this.pollingInterval = null;
                }

                this.container.innerHTML = '';
            }
        }
    }

    /**
     * Show loading state
     */
    showLoadingState() {
        console.log('🔧 Showing loading state');
        
        this.container.innerHTML = `
            <div class="deep-research-loading">
                <div class="loading-spinner"></div>
                <div class="loading-title">Research in Progress</div>
                <div class="loading-subtitle">
                    Our AI is conducting comprehensive research on this clock.<br>
                    This may take a few minutes as we gather detailed information.
                </div>
                <div class="loading-cost">
                    💰 Estimated cost: $0.01 - $0.05 per research
                </div>
            </div>
        `;
    }

    /**
     * Show processing state (for polling)
     */
    showProcessingState() {
        console.log('🔧 Showing processing state');
        
        this.container.innerHTML = `
            <div class="deep-research-loading">
                <div class="loading-spinner"></div>
                <div class="loading-title">Research in Progress</div>
                <div class="loading-subtitle">
                    Our AI is conducting comprehensive research on this clock.<br>
                    This may take a few minutes as we gather detailed information.
                </div>
                <div id="deep-research-processing" class="loading-status">
                    Research in progress…
                </div>
                <div class="loading-cost">
                    💰 Estimated cost: $0.01 - $0.05 per research
                </div>
            </div>
        `;
    }

    /**
     * Show error state
     * @param {string} message - Error message
     */
    showErrorState(message) {
        console.log('🔧 Showing error state:', message);
        
        this.container.innerHTML = `
            <div class="deep-research-error">
                <h4>❌ Research Failed</h4>
                <p>We encountered an error while conducting the research:</p>
                <p><strong>${message}</strong></p>
                <button class="retry-button" onclick="deepResearchService.showModal('${this.currentClockId}')">
                    🔄 Try Again
                </button>
            </div>
        `;
    }

    /**
     * Soft "still running" state — shown when the UI polling window elapses but the run is NOT failed
     * (it keeps going server-side). Reassures + offers a reload, instead of the red error modal.
     */
    showStillRunningState() {
        this.container.innerHTML = `
            <div class="deep-research-loading">
                <div class="loading-title">Still researching…</div>
                <div class="loading-subtitle">
                    This is taking longer than usual, but the research is still running on our servers.<br>
                    Reload this page in a few minutes to see the finished report.
                </div>
                <button class="retry-button" onclick="window.location.reload()">
                    🔄 Reload
                </button>
            </div>
        `;
    }

    /**
     * Neutral "no research yet" state for the #research deep-link when a clock has no research at all.
     */
    showNoResearchState() {
        this.container.innerHTML = `
            <div class="deep-research-loading">
                <div class="loading-title">No deep research yet</div>
                <div class="loading-subtitle">
                    Deep research hasn't been run for this clock yet.
                </div>
                <button class="retry-button" onclick="deepResearchService.showModal('${this.currentClockId}')">
                    🔬 Run deep research
                </button>
            </div>
        `;
    }

    /**
     * Start the research process
     * @param {string} clockId - The clock ID to research
     * @param {boolean} forceNew - Whether to force new research even if existing data exists
     */
    async startResearch(clockId, forceNew = false) {
        console.log('🔧 Starting research for clock:', clockId, 'forceNew:', forceNew);
        
        // Track research start
        if (window.analytics) {
            window.analytics.trackDeepResearch(clockId, 'start');
        }
        
        this.isProcessing = true;
        
        try {
            const DEEP_RESEARCH_API_URL = `${window.CONFIG.aws.deepResearch.baseUrl}${window.CONFIG.aws.deepResearch.endpoints.research}`;
            console.log('🌐 Research API URL:', DEEP_RESEARCH_API_URL);

            const response = await fetch(DEEP_RESEARCH_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    clockId: clockId,
                    forceNew: forceNew
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('🔧 Research response:', data);
            
            if (data.success) {
                if (data.status === 'completed') {
                    // Research already completed
                    this.showResearchResults(data.data);
                    this.isProcessing = false;
                } else if (data.status === 'started' || data.status === 'in_progress') {
                    // Research started or in progress, start polling
                    this.showProcessingState();
                    this.startPolling(clockId);
                    // Don't set isProcessing = false here, let polling handle it
                } else {
                    console.log('🔍 Unexpected status:', data.status, 'Full response:', data);
                    // Instead of throwing an error, try to handle gracefully
                    if (data.message && data.message.includes('already in progress')) {
                        console.log('🔄 Research already in progress, starting polling...');
                        this.showProcessingState();
                        this.startPolling(clockId);
                    } else {
                        throw new Error(data.message || `Unexpected research status: ${data.status}`);
                    }
                }
            } else {
                // Check if it's actually a success case with different message format
                if (data.message && data.message.includes('already in progress')) {
                    console.log('🔄 Research already in progress (success=false), starting polling...');
                    this.showProcessingState();
                    this.startPolling(clockId);
                } else {
                    throw new Error(data.message || 'Research failed');
                }
            }
            
        } catch (error) {
            console.error('❌ Failed to start research:', error);
            this.isProcessing = false;
            throw error;
        }
    }

    async startPolling(clockId) {
        console.log('🔄 Starting polling for clock:', clockId);
        
        const maxAttempts = 200; // ~16.7 min — must exceed the backend's 15-min ceiling (Lambda 900s / 180 polls) so the UI never quits before the run lands
        let attempts = 0;
        
        const poll = async () => {
            attempts++;
            console.log(`📊 Polling attempt ${attempts}/${maxAttempts}`);
            
            try {
                const DEEP_RESEARCH_API_URL = `${window.CONFIG.aws.deepResearch.baseUrl}${window.CONFIG.aws.deepResearch.endpoints.status}`;
                const response = await fetch(`${DEEP_RESEARCH_API_URL}?clockId=${clockId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                
                const result = await response.json();
                console.log('📥 Polling response:', result);
                
                if (result.success) {
                    if (result.status === 'completed') {
                        console.log('🎉 Research completed!');
                        this.showResearchResults(result.data);
                        this.isProcessing = false;
                        return; // Stop polling
                    } else if (result.status === 'failed') {
                        console.error('❌ Research failed:', result.error);
                        this.showErrorState(result.error || 'Research failed');
                        this.isProcessing = false;
                        return; // Stop polling
                    } else if (result.status === 'in_progress' || result.status === 'started') {
                        console.log('⏳ Research still in progress...');
                        const mins = Math.floor((attempts * 5) / 60);
                        this.updateProcessingMessage(attempts > 60
                            ? `Still researching — deep research can take up to ~15 minutes (${mins} min elapsed).`
                            : 'Research in progress… gathering and analysing sources.');
                        
                        if (attempts < maxAttempts) {
                            setTimeout(poll, 5000); // Poll again in 5 seconds
                        } else {
                            // NOT a failure: the run keeps going on our servers and writes the result when
                            // it finishes. Show a soft "still running" state instead of the red error modal.
                            console.warn('⏱️ UI polling window elapsed; research continues server-side.');
                            this.showStillRunningState();
                            this.isProcessing = false;
                        }
                    }
                } else {
                    throw new Error(result.message || 'Polling failed');
                }
                
            } catch (error) {
                console.error('❌ Polling error:', error);
                if (attempts < maxAttempts) {
                    setTimeout(poll, 5000); // Retry in 5 seconds
                } else {
                    this.showErrorState('Polling failed: ' + error.message);
                    this.isProcessing = false;
                }
            }
        };
        
        // Start polling
        poll();
    }

    updateProcessingMessage(message) {
        const processingElement = document.getElementById('deep-research-processing');
        if (processingElement) {
            processingElement.textContent = message;
        }
    }



    /**
     * Show existing research results (without sources/cost for viewing)
     * @param {Object} data - Research results data
     */
    showExistingResearchResults(data) {
        console.log('🔧 Showing existing research results (view mode):', data);
        
        const researchText = data.research_text || '';

        // Check if content is already HTML or needs formatting
        let formattedContent;
        if (researchText.includes('<') && researchText.includes('>')) {
            // Content is already HTML, use it directly
            formattedContent = researchText;
        } else {
            // Content is markdown/plain text, format it
            formattedContent = this.formatResearchContent(researchText, []);
        }
        
        this.container.innerHTML = `
            <div class="deep-research-data">
                <h1 class="research-title">Research</h1>

                <!-- System Explanation Section -->
                <div class="system-explanation">
                    <div class="system-explanation-header" onclick="toggleDeepResearchExplanation()">
                        <h4>How Research Works</h4>
                        <span class="system-explanation-toggle">▼</span>
                    </div>
                    <div class="system-explanation-content" style="display: none;">
                        <p>Our research system combines multiple data sources to provide comprehensive analysis:</p>
                        <ul>
                            <li><strong>User Generated Content:</strong> Original descriptions, titles, and metadata from clock posts</li>
                            <li><strong>AI Generated Tags:</strong> Automated categorization and tagging from our AI analysis</li>
                            <li><strong>API Derived Information:</strong> Factual data from enrichment services (Wikidata, OpenStreetMap, Google Maps)</li>
                            <li><strong>Specialized Research Model:</strong> Advanced AI that synthesizes all available information</li>
                        </ul>
                        <p><strong>Important:</strong> Research results may contain inaccuracies. We encourage knowledgeable logged-in users to edit and improve the content where appropriate.</p>
                    </div>
                </div>

                <div class="research-content">
                    <div class="research-content-header">
                        <h3>Research Content</h3>
                        <button class="edit-btn" data-fieldpath="research_text" data-target="research-text-content" title="Edit research content">
                            Edit
                        </button>
                    </div>
                    <div id="research-text-content">
                        ${formattedContent}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Show research results
     * @param {Object} data - Research results data
     */
    showResearchResults(data) {
        console.log('🔧 Showing research results:', data);
        
        const researchText = data.research_text || '';
        const sources = data.sources || [];
        const cost = data.cost || {};

        // Update the buttons to show "View" + "Re-run" since research is now complete
        if (window.clockViewPage && window.clockViewPage.updateDeepResearchButtons) {
            window.clockViewPage.updateDeepResearchButtons(true, data);
        }
        
        // Check if content is already HTML or needs formatting
        let formattedContent;
        if (researchText.includes('<') && researchText.includes('>')) {
            // Content is already HTML, use it directly
            formattedContent = researchText;
        } else {
            // Content is markdown/plain text, format it
            formattedContent = this.formatResearchContent(researchText, sources);
        }
        
        this.container.innerHTML = `
            <div class="deep-research-data">
                <h1 class="research-title">Research</h1>

                <!-- System Explanation Section -->
                <div class="system-explanation">
                    <div class="system-explanation-header" onclick="toggleDeepResearchExplanation()">
                        <h4>How Research Works</h4>
                        <span class="system-explanation-toggle">▼</span>
                    </div>
                    <div class="system-explanation-content" style="display: none;">
                        <p>Our research system combines multiple data sources to provide comprehensive analysis:</p>
                        <ul>
                            <li><strong>User Generated Content:</strong> Original descriptions, titles, and metadata from clock posts</li>
                            <li><strong>AI Generated Tags:</strong> Automated categorization and tagging from our AI analysis</li>
                            <li><strong>API Derived Information:</strong> Factual data from enrichment services (Wikidata, OpenStreetMap, Google Maps)</li>
                            <li><strong>Specialized Research Model:</strong> Advanced AI that synthesizes all available information</li>
                        </ul>
                        <p><strong>Important:</strong> Research results may contain inaccuracies. We encourage knowledgeable logged-in users to edit and improve the content where appropriate.</p>
                    </div>
                </div>

                <div class="research-content">
                    <div class="research-content-header">
                        <h3>Research Content</h3>
                        <button class="edit-btn" data-fieldpath="research_text" data-target="research-text-content" title="Edit research content">
                            Edit
                        </button>
                    </div>
                    <div id="research-text-content">
                        ${formattedContent}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Format research content with inline citations
     * @param {string} content - Raw research content
     * @param {Array} citations - Array of citations
     * @returns {string} Formatted HTML content
     */
    formatResearchContent(content, citations) {
        console.log('🔧 Formatting research content');
        
        // Convert markdown-style formatting to HTML
        let formatted = content
            // Headers
            .replace(/^# (.*$)/gim, '<h1>$1</h1>')
            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
            .replace(/^#### (.*$)/gim, '<h4>$1</h4>')
            // Bold text
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            // Italic text
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            // Links with inline citations
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
                const citationIndex = citations.findIndex(c => c.url === url);
                if (citationIndex !== -1) {
                    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}<sup>[${citationIndex + 1}]</sup></a>`;
                }
                return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            })
            // Line breaks
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br>');
        
        // Wrap in paragraphs
        formatted = `<p>${formatted}</p>`;
        
        return formatted;
    }

    /**
     * Render sources section
     * @param {Array} sources - Array of sources
     * @returns {string} HTML for sources section
     */
    renderSourcesSection(sources) {
        console.log('🔧 Rendering sources section with', sources.length, 'sources');
        
        const sourcesHtml = sources.map((source, index) => `
            <div class="source-item">
                <div class="source-number">${index + 1}</div>
                <div class="source-content">
                    <div class="source-title">${source.title || 'Source'}</div>
                    <div class="source-query">Query: ${source.query || 'Unknown'}</div>
                    ${source.url ? `<a href="${source.url}" target="_blank" rel="noopener noreferrer" class="source-url">${source.url}</a>` : ''}
                </div>
            </div>
        `).join('');
        
        return `
            <div class="research-sources">
                <div class="sources-header">
                    <h3>Sources & References</h3>
                    <button class="edit-btn" data-fieldpath="sources" data-target="sources-list" title="Edit sources">
                        Edit
                    </button>
                </div>
                <div id="sources-list" class="sources-list">
                    ${sourcesHtml}
                </div>
            </div>
        `;
    }

    /**
     * Setup edit event listeners using event delegation
     */
    setupEditEventListeners() {
        console.log('🔧 Setting up edit event listeners');
        
        // Use event delegation to handle dynamically added edit buttons
        this.container.addEventListener('click', (e) => {
            const target = e.target;
            
            // Handle edit button clicks
            if (target && target.classList.contains('edit-btn') && target.dataset.fieldpath) {
                e.preventDefault();
                e.stopPropagation();
                
                const fieldPath = target.dataset.fieldpath;
                const targetId = target.dataset.target;
                const span = targetId ? document.getElementById(targetId) : null;
                
                console.log('🔧 Edit button clicked:', fieldPath, targetId);
                
                this.handleResearchEdit(fieldPath, span);
            }
        });
        
        console.log('✅ Edit event listeners set up successfully');
    }

    /**
     * Handle research content editing
     * @param {string} fieldPath - The field path to edit
     * @param {HTMLElement} span - The span element containing the content
     */
    async handleResearchEdit(fieldPath, span) {
        console.log('🔧 Handling research edit:', fieldPath);
        console.log('🔧 Current clockId:', this.currentClockId);
        
        // Track research edit
        if (window.analytics) {
            window.analytics.trackDeepResearch(this.currentClockId, 'edit');
        }
        
        try {
            // Check if user is logged in
            const token = localStorage.getItem('authToken');
            if (!token) {
                alert('You must be logged in to edit research content');
                return;
            }
            
            // Get current content
            const current = this.getElementContent(span, fieldPath);
            const label = fieldPath.replace(/\./g, ' ').replace(/_/g, ' ');
            
            // For large content like research_text, show a textarea modal
            if (fieldPath === 'research_text') {
                this.showTextEditor(fieldPath, current, label);
            } else {
                // For smaller content, use prompt
                const newValue = prompt(`Edit ${label}`, current || '');
                if (newValue === null) return; // User cancelled
                
                const trimmed = newValue.trim();
                if (!trimmed) {
                    alert('Value cannot be empty');
                    return;
                }
                
                await this.updateResearchField(fieldPath, trimmed);
            }
            
        } catch (error) {
            console.error('❌ Edit failed:', error);
            alert(`Edit failed: ${error.message}`);
        }
    }

    /**
     * Get element content based on field type
     * @param {HTMLElement} span - The span element
     * @param {string} fieldPath - The field path
     * @returns {string} The current content
     */
    getElementContent(span, fieldPath) {
        if (!span) return '';
        
        if (fieldPath === 'sources') {
            // For sources, we need to reconstruct the JSON from the displayed HTML
            const sourceItems = span.querySelectorAll('.source-item');
            const sources = Array.from(sourceItems).map(item => {
                const title = item.querySelector('.source-title')?.textContent || '';
                const query = item.querySelector('.source-query')?.textContent?.replace('Query: ', '') || '';
                const url = item.querySelector('.source-url a')?.href || '';
                return { title, query, url };
            });
            return JSON.stringify(sources, null, 2);
        }
        
        // For research_text field, preserve HTML content
        if (fieldPath === 'research_text') {
            return span.innerHTML || '';
        }
        
        // For other fields, get text content
        return span.textContent || '';
    }

    /**
     * Show inline text editor for large content
     * @param {string} fieldPath - The field path
     * @param {string} current - Current content
     * @param {string} label - Field label
     */
    showTextEditor(fieldPath, current, label) {
        const researchTextContent = document.getElementById('research-text-content');
        if (!researchTextContent) {
            console.error('❌ Research text content element not found');
            return;
        }

        // Store original content for cancel
        const originalContent = researchTextContent.innerHTML;
        
        // Create inline editor with rich text capabilities
        const editorHtml = `
            <div class="inline-editor">
                <div class="inline-editor-header">
                    <h4>Editing ${label}</h4>
                    <div class="inline-editor-actions">
                        <button class="inline-editor-save" title="Save changes">Save</button>
                        <button class="inline-editor-cancel" title="Cancel editing">Cancel</button>
                    </div>
                </div>
                <div class="inline-editor-toolbar">
                    <button class="format-btn" data-format="bold" title="Bold (Ctrl+B)">B</button>
                    <button class="format-btn" data-format="italic" title="Italic (Ctrl+I)">I</button>
                    <button class="format-btn" data-format="underline" title="Underline (Ctrl+U)">U</button>
                    <button class="format-btn" data-format="link" title="Insert Link (Ctrl+K)">🔗</button>
                    <button class="format-btn" data-format="removeFormat" title="Remove Formatting">Clear</button>
                </div>
                <div class="inline-editor-content">
                    <div class="inline-editor-textarea" contenteditable="true" data-placeholder="Enter research content..."></div>
                </div>
                <div class="inline-editor-help">
                    <small>Use Ctrl+B for bold, Ctrl+I for italic, Ctrl+K for links, or use the toolbar above.</small>
                </div>
            </div>
        `;
        
        // Replace content with editor
        researchTextContent.innerHTML = editorHtml;
        
        // Setup event listeners
        const saveBtn = researchTextContent.querySelector('.inline-editor-save');
        const cancelBtn = researchTextContent.querySelector('.inline-editor-cancel');
        const textarea = researchTextContent.querySelector('.inline-editor-textarea');
        const formatBtns = researchTextContent.querySelectorAll('.format-btn');
        
        // Set the content in the textarea (preserving HTML)
        console.log('🔧 Setting editor content:', current);
        console.log('🔧 Content type check:', current.includes('<') && current.includes('>'));
        textarea.innerHTML = current;
        
        const cancelEdit = () => {
            researchTextContent.innerHTML = originalContent;
        };
        
        const saveEdit = async () => {
            const newValue = textarea.innerHTML.trim();
            if (!newValue || textarea.textContent.trim() === '') {
                alert('Content cannot be empty');
                return;
            }
            
            try {
                await this.updateResearchField(fieldPath, newValue);
            } catch (error) {
                console.error('❌ Save failed:', error);
                alert(`Save failed: ${error.message}`);
            }
        };
        
        // Rich text editor functionality
        const setupRichTextEditor = () => {
            // Format button handlers
            formatBtns.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const format = btn.dataset.format;
                    
                    if (format === 'link') {
                        const url = prompt('Enter URL:');
                        if (url && url.trim()) {
                            try {
                                new URL(url);
                                document.execCommand('createLink', false, url);
                                textarea.focus();
                            } catch (e) {
                                alert('Please enter a valid URL (e.g., https://example.com)');
                            }
                        }
                    } else if (format === 'removeFormat') {
                        document.execCommand('removeFormat', false, null);
                        textarea.focus();
                    } else {
                        document.execCommand(format, false, null);
                        textarea.focus();
                    }
                });
            });
            
            // Keyboard shortcuts
            textarea.addEventListener('keydown', (e) => {
                if (e.ctrlKey && e.key === 'b') {
                    e.preventDefault();
                    document.execCommand('bold', false, null);
                } else if (e.ctrlKey && e.key === 'i') {
                    e.preventDefault();
                    document.execCommand('italic', false, null);
                } else if (e.ctrlKey && e.key === 'u') {
                    e.preventDefault();
                    document.execCommand('underline', false, null);
                } else if (e.ctrlKey && e.key === 'k') {
                    e.preventDefault();
                    const url = prompt('Enter URL:');
                    if (url && url.trim()) {
                        try {
                            new URL(url);
                            document.execCommand('createLink', false, url);
                        } catch (e) {
                            alert('Please enter a valid URL (e.g., https://example.com)');
                        }
                    }
                }
            });
            
            // Paste handling - strip formatting to prevent issues
            textarea.addEventListener('paste', (e) => {
                e.preventDefault();
                const text = (e.clipboardData || window.clipboardData).getData('text/plain');
                document.execCommand('insertText', false, text);
            });
        };
        
        saveBtn.addEventListener('click', saveEdit);
        cancelBtn.addEventListener('click', cancelEdit);
        
        // Setup rich text editor
        setupRichTextEditor();
        
        // Focus the textarea
        textarea.focus();
        
        // Handle escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                cancelEdit();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);
    }

    /**
     * Update research field via API
     * @param {string} fieldPath - The field path
     * @param {string} newValue - The new value
     */
    async updateResearchField(fieldPath, newValue) {
        console.log('🔧 Updating research field:', fieldPath);
        
        const token = localStorage.getItem('authToken');
        if (!token) {
            throw new Error('You must be logged in to edit');
        }
        
        // Get clock ID from current context or URL parameters
        let clockId = this.currentClockId;
        if (!clockId) {
            // Try to get from URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            clockId = urlParams.get('id');
            console.log('🔧 Retrieved clockId from URL:', clockId);
        }
        
        if (!clockId) {
            throw new Error('Clock ID not found. Please refresh the page and try again.');
        }
        
        const payload = {
            fieldPath: fieldPath,
            newValue: newValue,
            editReason: 'Research content correction from UI'
        };

        const DEEP_RESEARCH_EDITOR_API_URL = `${window.CONFIG.aws.deepResearchEditor.baseUrl}${window.CONFIG.aws.deepResearchEditor.endpoints.edit.replace('{clockId}', clockId)}`;
        const response = await fetch(DEEP_RESEARCH_EDITOR_API_URL, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || `Edit failed: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('✅ Research field updated:', result);
        
        // Update the UI
        this.updateElementContent(fieldPath, newValue);
        
        // Show success message
        this.showEditSuccess(fieldPath);
    }

    /**
     * Update element content in the UI
     * @param {string} fieldPath - The field path
     * @param {string} newValue - The new value
     */
    updateElementContent(fieldPath, newValue) {
        if (fieldPath === 'research_text') {
            // Update the research content div specifically
            const researchTextContent = document.getElementById('research-text-content');
            if (researchTextContent) {
                // Check if the content is already HTML (contains tags)
                if (newValue.includes('<') && newValue.includes('>')) {
                    // Content is already HTML, use it directly
                    researchTextContent.innerHTML = newValue;
                } else {
                    // Content is plain text, format it
                    const sources = this.getCurrentSources();
                    const formattedContent = this.formatResearchContent(newValue, sources);
                    researchTextContent.innerHTML = formattedContent;
                }
            }
        } else if (fieldPath === 'sources') {
            // Update the sources section
            const sources = JSON.parse(newValue);
            const sourcesSection = document.querySelector('.sources-section');
            if (sourcesSection) {
                sourcesSection.innerHTML = this.renderSourcesSection(sources);
            }
        }
    }

    /**
     * Get current sources from the UI
     * @returns {Array} Current sources array
     */
    getCurrentSources() {
        const sourceItems = document.querySelectorAll('.source-item');
        return Array.from(sourceItems).map(item => {
            const title = item.querySelector('.source-title')?.textContent || '';
            const query = item.querySelector('.source-query')?.textContent?.replace('Query: ', '') || '';
            const url = item.querySelector('.source-url a')?.href || '';
            return { title, query, url };
        });
    }

    /**
     * Show edit success message
     * @param {string} fieldPath - The field that was edited
     */
    showEditSuccess(fieldPath) {
        const label = fieldPath.replace(/\./g, ' ').replace(/_/g, ' ');
        
        // Create success message
        const successMsg = document.createElement('div');
        successMsg.className = 'edit-success-message';
        successMsg.textContent = `✅ ${label} updated successfully`;
        successMsg.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #4CAF50;
            color: white;
            padding: 12px 20px;
            border-radius: 4px;
            z-index: 10000;
            font-size: 14px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        `;
        
        document.body.appendChild(successMsg);
        
        // Remove after 3 seconds
        setTimeout(() => {
            if (document.body.contains(successMsg)) {
                document.body.removeChild(successMsg);
            }
        }, 3000);
    }
}

// Initialize the service when DOM is ready
let deepResearchService;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        console.log('🔧 DOM loaded, initializing research service');
        deepResearchService = new DeepResearchService();
    });
} else {
    console.log('🔧 DOM already loaded, initializing research service');
    deepResearchService = new DeepResearchService();
}

// Global function for triggering research
window.startDeepResearch = function(clockId, forceNew = false) {
    console.log('🔧 startDeepResearch called with clockId:', clockId, 'forceNew:', forceNew);
    if (deepResearchService) {
        deepResearchService.showModal(clockId, forceNew);
    } else {
        console.error('❌ Research service not initialized');
    }
};

// Toggle function for research system explanation collapsible section
window.toggleDeepResearchExplanation = function() {
    const content = document.querySelector('.system-explanation-content');
    const toggle = document.querySelector('.system-explanation-toggle');

    if (content && toggle) {
        if (content.style.display === 'none') {
            content.style.display = 'block';
            toggle.textContent = '▲';
        } else {
            content.style.display = 'none';
            toggle.textContent = '▼';
        }
    }
};

console.log('✅ Research service script loaded');
